import { booleanAttribute, ChangeDetectionStrategy, Component, computed, effect, forwardRef, inject, input, linkedSignal, untracked } from '@angular/core';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';

import { LayoutTplComponent } from '@shared/_views/layout-tpl/layout-tpl.component';
import { BaseMin } from '@base/base-min';
import { Task, LayoutElement, createLayoutElement, TTaskCardName, TaskCardsMap, TaskTypeDef, RecursivePartial, SM, Guid, TASK_DEFAULT_LAYOUT } from '@models';
import { CacheService } from '@services/cache.service';

@Component({
    selector: 'vz-task-card, [vz-task-card]',
    templateUrl: './task.component.html',
    styleUrls: ['./task.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NzButtonModule, NzCheckboxModule, NzTooltipModule, NzDropDownModule,
        forwardRef(() => LayoutTplComponent)
    ],
    host: {
        '[style.height]': 'hostHeight()',
        'class': 'layout vertical'
    }
})
export class TaskCardComponent extends BaseMin {
    readonly _task          = input<Task>(undefined, { alias: 'task' });
    readonly task           = linkedSignal<Task | undefined>(() => this._task());

    readonly _typeDef       = input<RecursivePartial<TaskTypeDef>>(undefined, { alias: 'typeDef' });
    readonly typeDef        = linkedSignal<RecursivePartial<TaskTypeDef> | undefined>(() => this._typeDef());

    readonly taskId         = input<string>();
    readonly taskCardName   = input<TTaskCardName>();
    readonly options        = input<any>();
    readonly objectsMap     = input<SM<any>>();
    readonly fixedHeight    = input<number>();
    readonly noBorder       = input<boolean, unknown>(false, { transform: booleanAttribute });

    readonly hostHeight     = computed<string | undefined>(() => this.fixedHeight() ? this.fixedHeight() + 'px' : undefined);

    readonly cardTpl        = computed<LayoutElement>(() => {
        const taskCardName = this.taskCardName();
        if (taskCardName) {
            return createLayoutElement(TaskCardsMap[taskCardName]);
        }
        else {
            const cn = this._store?.getState('prefs').kanbanCard || 'clear';
            return createLayoutElement(TaskCardsMap[cn]);
        }
    });

    private readonly _cache = inject(CacheService);

    constructor() {
        super();
        // console.log('-[TaskCard,Cache]---> constructor:', getUuid());
    }

    private readonly _1 = effect(() => {
        const taskId = this.taskId();
        // console.log('-[TaskCard,Cache]---> effect_1, taskId:', taskId, '_tid:', this._tid);
        const task = untracked(this.task);
        const orgId = untracked(this._orgId);
        // console.log('-[TaskCard,Cache]---> effect_1, task:', task, '_t:', this._t, '==', task == this._t);
        if (taskId && orgId  && this._store && !task) {
            this._error.set(undefined);
            this._S.task = this._Loading(this._cache.get('task', taskId)).subscribe({
                next: ci => {
                    this.task.set(ci.item);
                    this._Changed();
                    this.getTaskTypeDef(ci.item?.typeId);
                },
                error: err => this._Error(err)
            });
        }
        else if (task && !untracked(this._typeDef)) {
            this.getTaskTypeDef(task.typeId);
        }
    });

    getTaskTypeDef(typeId?: Guid): void {
        this.__U(['ttd']);
        // console.log('-[TaskCard,Cache]---> getTaskTypeDef, typeId:', typeId, '_ttid:', this._ttid);
        if (typeId) {
            this._S.ttd = this._cache.watch('taskType', typeId).subscribe(st => {
                // console.log(`[cache.getTaskTypeDef]--- > ${typeId} st:`, st);
                if (st.err) {
                    this._Error(st.err);
                }
                else if (!st.loading || st.item) {
                    this.typeDef.set(st.item || new TaskTypeDef({ layout: TASK_DEFAULT_LAYOUT }));
                    this._Changed();
                }
            });
        }
    }

}

